import {
  CUSTOM_FEE_TYPE,
  type Fee,
  type FeeOverrides,
  type FeeSection,
  type FlattenedFee,
} from 'app/types/costAndFees.type';
import { parseNumericAmount } from './amountUtils'; // Import helper from new amount utils file

/**
 * Flattens nested fee structures into a single array for easier processing.
 */
export const flattenFees = (groupedFees: FeeOverrides | null | undefined): Array<FlattenedFee> => {
  if (!groupedFees) {
    return [];
  }
  const flattenedFees: Array<FlattenedFee> = [];

  (Object.keys(groupedFees) as Array<keyof FeeOverrides>).forEach((frequencyKey) => {
    const section = groupedFees[frequencyKey];
    if (section) {
      section.feeGroup?.forEach((feeGroup) => {
        feeGroup.fees?.forEach((fee) => {
          flattenedFees.push({
            ...fee,
            frequency: frequencyKey,
            frequencyDisplayName: section.displayName || '',
            frequencySubText: '',
            feeGroupType: feeGroup.type,
            feeGroupDisplayName: feeGroup.displayName || '',
          });
        });
      });
    }
  });
  return flattenedFees;
};

/**
 * Expands a flattened fee array back into the nested FeeOverrides structure.
 */
export const expandFees = (flattenedFees: Array<FlattenedFee>): FeeOverrides => {
  const expandedFees: FeeOverrides = {};

  flattenedFees.forEach((fee) => {
    const frequency = fee.frequency as keyof FeeOverrides;

    if (!expandedFees[frequency]) {
      expandedFees[frequency] = {
        displayName: fee.frequencyDisplayName,
        feeGroup: [],
      };
    }

    const currentFeeSection = expandedFees[frequency]!;

    let existingFeeGroup = currentFeeSection.feeGroup.find((group) => group.type === fee.feeGroupType);

    if (!existingFeeGroup) {
      existingFeeGroup = {
        type: fee.feeGroupType,
        displayName: fee.feeGroupDisplayName,
        fees: [],
      };
      currentFeeSection.feeGroup.push(existingFeeGroup);
    }

    const feeAlreadyExists = existingFeeGroup.fees.some((f) => f.type === fee.type);

    if (fee.type === CUSTOM_FEE_TYPE || !feeAlreadyExists) {
      const {
        frequency: _f,
        frequencyDisplayName: _fdn,
        feeGroupType: _fgt,
        feeGroupDisplayName: _fgdn,
        frequencySubText: _fst,
        ...originalFeeData
      } = fee;
      existingFeeGroup.fees.push(originalFeeData as Fee);
    }
  });

  return expandedFees;
};

/**
 * Combines multiple FeeOverrides structures, typically from different selectors (e.g., units).
 * For overlapping non-custom fees, it generally takes the minimum 'min' and maximum 'max' of amounts/counters.
 * Keeps the first encountered options array. Adds custom fees.
 */
export const combineFees = (feeOverridesArray: Array<FeeOverrides | null | undefined>): FeeOverrides => {
  const allFlattenedFees: Array<FlattenedFee> = feeOverridesArray
    .filter((override): override is FeeOverrides => !!override)
    .flatMap(flattenFees);

  const combinedFeeMap = new Map<string, FlattenedFee>();

  allFlattenedFees.forEach((fee) => {
    const feeKey = `${fee.type}-${fee.frequency}-${fee.feeGroupType}`;
    const existingFee = combinedFeeMap.get(feeKey);

    if (fee.type === CUSTOM_FEE_TYPE || !existingFee) {
      combinedFeeMap.set(feeKey, { ...fee });
      return;
    }

    if (!('displayText' in existingFee.amount) && !('displayText' in fee.amount)) {
      const existingNumericAmount = parseNumericAmount(existingFee.amount);
      const currentNumericAmount = parseNumericAmount(fee.amount);

      const combinedMin = Math.min(existingNumericAmount.min, currentNumericAmount.min);
      let combinedMax: number | null;

      if (existingNumericAmount.max === null || currentNumericAmount.max === null) {
        combinedMax = null;
      } else {
        combinedMax = Math.max(existingNumericAmount.max, currentNumericAmount.max);
      }
      existingFee.amount = { min: combinedMin, max: combinedMax };
    }

    if (existingFee.counter && fee.counter) {
      const existingMinCounter = existingFee.counter.min ?? 0;
      const currentMinCounter = fee.counter.min ?? 0;
      const existingMaxCounter = existingFee.counter.max;
      const currentMaxCounter = fee.counter.max;

      let combinedMaxCounter: number | null;
      if (existingMaxCounter === null || currentMaxCounter === null) {
        combinedMaxCounter = null; // If either is unbounded, result is unbounded
      } else {
        combinedMaxCounter = Math.max(existingMaxCounter, currentMaxCounter);
      }

      existingFee.counter = {
        min: Math.min(existingMinCounter, currentMinCounter),
        max: combinedMaxCounter,
      };
    }

    combinedFeeMap.set(feeKey, existingFee);
  });

  return expandFees(Array.from(combinedFeeMap.values()));
};

/**
 * Checks if a FeeSection contains any fees.
 */
export const hasFeesInSection = (section: FeeSection | undefined | null): boolean => {
  if (!section || !section.feeGroup) {
    return false;
  }
  return section.feeGroup.some((group) => group.fees && group.fees.length > 0);
};
